/**
 * Settings Store
 * State management for app settings with localStorage persistence
 */

import { useState, useCallback, createContext, useContext, useMemo } from 'react';

const SettingsContext = createContext(null);

// Storage key prefix
const STORAGE_PREFIX = 'muzie_admin_';

// Default values
const DEFAULTS = {
  pollingInterval: 5000,
  pageSize: {
    rooms: 10,
    recordings: 10,
    usage: 10,
    billing: 10,
    members: 10,
  },
  // Per-table visible columns, keyed by table name. A table with no entry
  // falls back to the defaults its own column registry declares.
  visibleColumns: {},
  // Per-table filter + sort selections, keyed by table name.
  tableFilters: {},
  // Add other persisted preferences here
  sidebarCollapsed: false,
  theme: 'system', // 'light', 'dark', 'system'
};

/**
 * Get a value from localStorage with fallback to default
 */
function getStoredValue(key, defaultValue) {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (stored === null) return defaultValue;

    // Handle objects/arrays
    if (typeof defaultValue === 'object') {
      return { ...defaultValue, ...JSON.parse(stored) };
    }
    // Handle numbers
    if (typeof defaultValue === 'number') {
      return parseInt(stored, 10);
    }
    // Handle booleans
    if (typeof defaultValue === 'boolean') {
      return stored === 'true';
    }
    return stored;
  } catch {
    return defaultValue;
  }
}

/**
 * Set a value in localStorage
 */
function setStoredValue(key, value) {
  try {
    const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, stringValue);
  } catch (e) {
    console.warn('Failed to save setting to localStorage:', e);
  }
}

export function SettingsProvider({ children }) {
  // Polling interval
  const [pollingInterval, setPollingIntervalState] = useState(
    () => getStoredValue('pollingInterval', DEFAULTS.pollingInterval)
  );

  // Page sizes for different tables
  const [pageSizes, setPageSizesState] = useState(
    () => getStoredValue('pageSize', DEFAULTS.pageSize)
  );

  // Visible columns per table
  const [visibleColumns, setVisibleColumnsState] = useState(
    () => getStoredValue('visibleColumns', DEFAULTS.visibleColumns)
  );

  // Filter + sort selections per table
  const [tableFilters, setTableFiltersState] = useState(
    () => getStoredValue('tableFilters', DEFAULTS.tableFilters)
  );

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(
    () => getStoredValue('sidebarCollapsed', DEFAULTS.sidebarCollapsed)
  );

  // Theme preference
  const [theme, setThemeState] = useState(
    () => getStoredValue('theme', DEFAULTS.theme)
  );

  // Setters with localStorage persistence
  const setPollingInterval = useCallback((interval) => {
    setStoredValue('pollingInterval', interval);
    setPollingIntervalState(interval);
  }, []);

  const setPageSize = useCallback((tableName, size) => {
    setPageSizesState((prev) => {
      const updated = { ...prev, [tableName]: size };
      setStoredValue('pageSize', updated);
      return updated;
    });
  }, []);

  const setTableColumns = useCallback((tableName, columns) => {
    setVisibleColumnsState((prev) => {
      const updated = { ...prev, [tableName]: columns };
      setStoredValue('visibleColumns', updated);
      return updated;
    });
  }, []);

  const setTableFilters = useCallback((tableName, filters) => {
    setTableFiltersState((prev) => {
      const updated = { ...prev, [tableName]: filters };
      setStoredValue('tableFilters', updated);
      return updated;
    });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed) => {
    setStoredValue('sidebarCollapsed', collapsed);
    setSidebarCollapsedState(collapsed);
  }, []);

  const setTheme = useCallback((newTheme) => {
    setStoredValue('theme', newTheme);
    setThemeState(newTheme);
  }, []);

  // Helper to get page size for a specific table
  const getPageSize = useCallback((tableName) => {
    return pageSizes[tableName] || DEFAULTS.pageSize[tableName] || 10;
  }, [pageSizes]);

  // null when the user has never chosen — the caller then uses its own defaults.
  const getTableColumns = useCallback(
    (tableName) => visibleColumns[tableName] ?? null,
    [visibleColumns],
  );

  const getTableFilters = useCallback(
    (tableName) => tableFilters[tableName] ?? null,
    [tableFilters],
  );

  const value = useMemo(() => ({
    // Polling
    pollingInterval,
    setPollingInterval,

    // Page sizes
    pageSizes,
    getPageSize,
    setPageSize,

    // Visible columns
    visibleColumns,
    getTableColumns,
    setTableColumns,

    // Filters + sort
    tableFilters,
    getTableFilters,
    setTableFilters,

    // Sidebar
    sidebarCollapsed,
    setSidebarCollapsed,

    // Theme
    theme,
    setTheme,
  }), [
    pollingInterval, setPollingInterval,
    pageSizes, getPageSize, setPageSize,
    visibleColumns, getTableColumns, setTableColumns,
    tableFilters, getTableFilters, setTableFilters,
    sidebarCollapsed, setSidebarCollapsed,
    theme, setTheme,
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    // Return defaults if provider is not mounted
    return {
      pollingInterval: DEFAULTS.pollingInterval,
      setPollingInterval: () => {},
      pageSizes: DEFAULTS.pageSize,
      getPageSize: (tableName) => DEFAULTS.pageSize[tableName] || 10,
      setPageSize: () => {},
      visibleColumns: DEFAULTS.visibleColumns,
      getTableColumns: () => null,
      setTableColumns: () => {},
      tableFilters: DEFAULTS.tableFilters,
      getTableFilters: () => null,
      setTableFilters: () => {},
      sidebarCollapsed: DEFAULTS.sidebarCollapsed,
      setSidebarCollapsed: () => {},
      theme: DEFAULTS.theme,
      setTheme: () => {},
    };
  }
  return context;
}

/**
 * Convenience hook for table pagination with persistence
 * @param {string} tableName - Unique identifier for the table
 * @returns {{ rowsPerPage: number, setRowsPerPage: (size: number) => void }}
 */
export function usePageSize(tableName) {
  const { getPageSize, setPageSize } = useSettings();

  const rowsPerPage = getPageSize(tableName);

  const setRowsPerPage = useCallback((size) => {
    setPageSize(tableName, size);
  }, [tableName, setPageSize]);

  return { rowsPerPage, setRowsPerPage };
}

/**
 * Convenience hook for a table's visible columns, persisted like page size.
 *
 * @param {string} tableName - Unique identifier for the table
 * @param {Array<{key: string, label: string, default?: boolean, fixed?: boolean}>} columns
 * @returns {{ visible: string[], isVisible: (key:string)=>boolean, toggle: (key:string)=>void, reset: ()=>void, columns: Array }}
 */
export function useTableColumns(tableName, columns) {
  const { getTableColumns, setTableColumns } = useSettings();

  const stored = getTableColumns(tableName);
  // `fixed` columns are always on and are never offered in the picker, so a
  // stored selection can never hide the row's identity or its actions.
  const fallback = columns.filter((c) => c.fixed || c.default !== false).map((c) => c.key);
  const chosen = stored ?? fallback;
  const visible = [
    ...new Set([...columns.filter((c) => c.fixed).map((c) => c.key), ...chosen]),
  ].filter((k) => columns.some((c) => c.key === k));

  const isVisible = useCallback((key) => visible.includes(key), [visible]);

  const toggle = useCallback((key) => {
    const col = columns.find((c) => c.key === key);
    if (!col || col.fixed) return;
    const next = visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key];
    setTableColumns(tableName, next);
  }, [columns, visible, setTableColumns, tableName]);

  const reset = useCallback(() => setTableColumns(tableName, fallback), [setTableColumns, tableName, fallback]);

  return { visible, isVisible, toggle, reset, columns };
}

/**
 * Convenience hook for a table's persisted filters and sort.
 *
 * Free-text search is deliberately NOT persisted: coming back to a list that is
 * silently filtered by a search you forgot typing reads as missing data. The
 * dropdown filters and the sort are persisted, because those are choices you
 * make once and expect to stick.
 *
 * @param {string} tableName
 * @param {object} defaults - shape of the persisted selection
 */
export function useTableFilters(tableName, defaults) {
  const { getTableFilters, setTableFilters } = useSettings();
  const stored = getTableFilters(tableName);
  // Memoised so the setters below keep a stable identity across renders.
  const filters = useMemo(() => ({ ...defaults, ...(stored ?? {}) }), [defaults, stored]);

  const setFilter = useCallback((key, value) => {
    setTableFilters(tableName, { ...filters, [key]: value });
  }, [filters, setTableFilters, tableName]);

  /**
   * Update several keys at once. Two sequential setFilter calls do NOT compose:
   * each spreads the `filters` captured by the current render, so the second
   * overwrites the first. Anything changing more than one key (sort + direction,
   * say) must go through here.
   */
  const setManyFilters = useCallback((partial) => {
    setTableFilters(tableName, { ...filters, ...partial });
  }, [filters, setTableFilters, tableName]);

  const resetFilters = useCallback(() => {
    setTableFilters(tableName, defaults);
  }, [setTableFilters, tableName, defaults]);

  return { filters, setFilter, setManyFilters, resetFilters };
}

export default useSettings;
